// The repertoire (2026-10-09): the ranking's variations grouped under their
// family, per colour. A family's score is the average score after the
// opening over its scanned games, every variation's games together. Families
// with `RANKED_MIN_GAMES` scanned games are listed most played first; the
// rest are set apart, also most played first.
import { RANKED_MIN_GAMES, type OpeningRow, type OpeningsRanking } from '@chess/api-insights';
import type { Color } from '@chess/api-model';

export interface RepertoireFamily {
  family: string;
  colour: Color;
  games: number;
  scanned: number;
  /** Pawns, the average score after the opening over the scanned games; undefined when none is scanned. */
  averageScore?: number;
  /** The family's variations, most played first. */
  variations: OpeningRow[];
}

export interface Repertoire {
  /** Families with enough scanned games, most played first. */
  ranked: RepertoireFamily[];
  /** The others, most played first. */
  rest: RepertoireFamily[];
  games: number;
}

/** The family of a variation row: its `family`, or the name before ": ". */
const familyOf = (r: OpeningRow) => r.family ?? r.opening.split(': ')[0];

export function repertoireOf(ranking: OpeningsRanking, colour: Color): Repertoire {
  const families = new Map<string, RepertoireFamily & { sum: number }>();
  for (const r of [...ranking.scored, ...ranking.unscanned, ...ranking.tooFew]) {
    if (r.colour !== colour) continue;
    const family = familyOf(r);
    let f = families.get(family);
    if (!f) families.set(family, (f = { family, colour, games: 0, scanned: 0, sum: 0, variations: [] }));
    f.games += r.games;
    f.variations.push(r);
    if (r.averageScore !== undefined && r.scanned) {
      f.scanned += r.scanned;
      f.sum += r.averageScore * r.scanned;
    }
  }
  const all = [...families.values()].map(({ sum, ...f }) => ({
    ...f,
    averageScore: f.scanned ? sum / f.scanned : undefined,
    variations: f.variations.sort((a, b) => b.games - a.games || a.opening.localeCompare(b.opening)),
  }));
  const byGames = (a: RepertoireFamily, b: RepertoireFamily) => b.games - a.games || a.family.localeCompare(b.family);
  return {
    ranked: all.filter((f) => f.scanned >= RANKED_MIN_GAMES).sort(byGames),
    rest: all.filter((f) => f.scanned < RANKED_MIN_GAMES).sort(byGames),
    games: all.reduce((n, f) => n + f.games, 0),
  };
}
